import { Component, computed, effect, inject, input } from '@angular/core';
import { HelpButton, Button, Badge, Skeleton, InputError } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { SupplierContractScoreColorPipe } from '../../pipes/supplier-contract-score-color-pipe';
import { SupplierContractScorePipe } from '../../pipes/supplier-contract-score-pipe';
import { DatePipe } from '@angular/common';
import { LocationNamePipe } from '../../../locations/pipes/location-name-pipe';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  getEnergyInterfaces,
  getLocations,
  SupplierContractData,
} from '../../stores/supplier-contracts.store';
import { GermanNumberPipe } from '../../../../shared/pipes/german-number-pipe';
import { EnergyInterfacesStore } from '../../../energy-interfaces/stores/energy-interfaces.store';
import { LocationsStore } from '../../../locations/store/locations.store';
import { isNotNull } from '../../../../core/helper/type-helper';
import {
  ForecastKey,
  SupplierContractForecastStore,
} from '../../stores/supplier-contract-forecast.store';

@Component({
  selector: 'app-supplier-contract-details-edit-form',
  templateUrl: './supplier-contract-details-edit-form.html',
  imports: [
    HelpButton,
    Button,
    NgIcon,
    SupplierContractScoreColorPipe,
    SupplierContractScorePipe,
    Badge,
    DatePipe,
    LocationNamePipe,
    TranslocoPipe,
    GermanNumberPipe,
    Skeleton,
    InputError,
  ],
})
export class SupplierContractDetailsEditForm {
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private locationsStore = inject(LocationsStore);
  private forecastStore = inject(SupplierContractForecastStore);

  supplierContract = input.required<SupplierContractData>();
  organizationId = input.required<string>();
  supplierContractId = input.required<string>();

  protected currentYear = new Date().getFullYear();

  protected latestPossibleTerminationDate = computed(() => {
    const terminationPeriodEndDate = this.supplierContract().terminationPeriodEndDate;
    if (!terminationPeriodEndDate) {
      return null;
    }
    const endDate = new Date(terminationPeriodEndDate);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
    return endDate;
  });

  protected locations = computed(() => {
    const ids = getLocations(
      this.supplierContract(),
      this.energyInterfacesStore.energyInterfaces() ?? [],
    );
    const allLocations = this.locationsStore.locations() ?? [];
    return ids
      .map((id) => allLocations.find((location) => location.id === id)?.data)
      .filter(isNotNull);
  });

  protected energyInterfaces = computed(() =>
    getEnergyInterfaces(
      this.supplierContract(),
      this.energyInterfacesStore.energyInterfaces() ?? [],
    ),
  );

  protected price = computed(() => {
    const forecastKey: ForecastKey = `${this.supplierContractId()}-${this.currentYear}-ALL`;
    return this.forecastStore.prices()[forecastKey] ?? null;
  });

  protected totalConsumption = computed(() => this.price()?.data?.totalConsumption ?? 0);

  constructor() {
    effect(() => {
      if (this.price() === null) {
        this.forecastStore.loadPrice(this.supplierContractId(), this.currentYear, null);
      }
    });
  }
}
